import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDeleteConfiguration } from '@partful/features/configurations/lib/useDeleteConfiguration';
import { useDeleteCADJob } from '@partful/features/job/lib/useDeleteCADJob';
import { Button } from '@partful/shared/ui/Button';
import { useConfirmation } from '@partful/shared/ui/Confirmation/useConfirmation';
import { useSnackBar } from '@partful/shared/ui/SnackBar/useSnackBar';
import { Job } from '@providers/job/types';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './ProductCard.module.scss';

export default function ProcessingFailedCard({
  job,
  redirectURL,
  canDelete,
}: Readonly<{ job: Job; redirectURL: string; canDelete: boolean }>) {
  const { t } = useTranslation('productLibrary');
  const { jobType, status, productName, runID } = job;

  if (jobType === 'CAD') {
    return (
      <ProcessingFailed
        job={job}
        redirectURL={redirectURL}
        header={t('errorProcessingFile', { productName })}
        subHeader={t('errorReference', { runID })}
        status={status}
        canDelete={canDelete}
      />
    );
  }

  if (jobType === 'SalesBOM') {
    return (
      <ProcessingFailed
        job={job}
        redirectURL={redirectURL}
        header={t('salesBOM.failed', { productName })}
        subHeader={t('errorReference', { runID })}
        status={status}
        canDelete={canDelete}
      />
    );
  }

  if (jobType === 'PartCategories') {
    return (
      <ProcessingFailed
        job={job}
        redirectURL={redirectURL}
        header={t('partsCategories.failed', { productName })}
        subHeader={t('errorReference', { runID })}
        status={status}
      />
    );
  }

  /* istanbul ignore next */
  return null; // filtering above should prevent getting here...
}

interface ProcessingFailedCardProps {
  job: Job;
  redirectURL: string;
  header: string;
  subHeader: string;
  status: string;
  canDelete?: boolean;
}

function ProcessingFailed({
  job,
  redirectURL,
  header,
  subHeader,
  status,
  canDelete,
}: Readonly<ProcessingFailedCardProps>) {
  const { t } = useTranslation('createProduct');
  const { confirm, close } = useConfirmation();
  const { showSnackBar } = useSnackBar();
  const { mutateAsync: deleteConfiguration } = useDeleteConfiguration();
  const { mutateAsync: deleteCADJob } = useDeleteCADJob();

  const onDeleteJob = async () => {
    confirm({
      title: t('configuration.deleteConfiguration.title', { productName: job.productName }),
      message: t('configuration.deleteConfiguration.message'),
      confirmText: t('configuration.deleteConfiguration.confirmText'),
      cancelText: t('configuration.deleteConfiguration.cancelText'),
      onConfirm: async () => {
        if (job.configurationPID) {
          await deleteConfiguration({ accountId: job.account, configurationPID: job.configurationPID });
        } else {
          await deleteCADJob({ accountId: job.account, jobId: job.runID });
        }
        close();
        showSnackBar({
          severity: 'success',
          message: t('configuration.deleteConfiguration.successMessage', { productName: job.productName }),
        });
      },
    });
  };

  return (
    <div className={styles.processingCardContent}>
      <div className={styles.failedCardToolbar}>
        {canDelete && (
          <Button variant="destructive" onClick={onDeleteJob} aria-label={`Delete ${job.productName}`}>
            {t('configuration.deleteButton')}
          </Button>
        )}
      </div>
      <Link to={redirectURL} className={styles.processingCardContentLink} data-testid={`processingFailed-${status}`}>
        <FontAwesomeIcon icon={faCircleExclamation} size="5x" color="#F64F46" />
        <h3>{header}</h3>
        <h2>{job.productName}</h2>
        <h4>
          <Trans>{subHeader}</Trans>
        </h4>
      </Link>
    </div>
  );
}
